<template>
  <div class="">
    <section-title content="Información de Licencia(s)" :has-line="true" />
    <p
      v-if="activateSpecialistLicenseFilterByHealthDepartment"
      class="text-gray"
    >
      <em class="far fa-info-circle mr-1" />
      Las licencias correspondientes al Departamento de Salud, deben ser
      agregadas mediante la acción Consultar DS
    </p>
    <div v-if="hasLicenses">
      <div v-for="(license, idx) in licenses" :key="`type-${idx}`">
        <custom-callout
          :border-color="'gray'"
          class-main="mt-1 rounded mb-2"
          :has-toggle="false"
          :opened="false"
        >
          <div class="p-2 mr-md-0 align-items-center">
            <div class="row">
              <div class="col-md-2 col-sm-12">
                <div class="color-gray-6 font-14">Especialidad:</div>
                <div class="color-black font-16">
                  {{ license.specialtyTypeName | empty }}
                </div>
              </div>
              <div class="col-md-2 col-sm-12">
                <div class="color-gray-6 font-14">Número de Licencia:</div>
                <div class="color-black font-16">
                  {{ license.licenseNo | empty }}
                </div>
              </div>
              <div class="col-md-2 col-sm-12">
                <div class="color-gray-6 font-14">
                  Fecha de Expiración Licencia:
                </div>
                <div
                  class="color-green font-16"
                  :class="{
                    'color-red': hasDocumentExpired(license.licenseExpireDate),
                  }"
                >
                  {{ license.licenseExpireDate | date }}
                </div>
              </div>
              <div class="col-md-2 col-sm-12">
                <div class="color-gray-6 font-14">
                  Fecha de Expiración Background Check:
                </div>
                <div
                  class="color-green font-16"
                  :class="{
                    'color-red': hasDocumentExpired(
                      license.backgroundExpireDate
                    ),
                  }"
                >
                  {{ license.backgroundExpireDate | date }}
                </div>
              </div>
              <div v-if="license.provisionalFlag" class="col">
                <div class="color-black font-16 mt-2">
                  <check-box
                    class="checkbox"
                    disabled
                    text="Provisional"
                    :value="license.provisionalFlag"
                  />
                </div>
              </div>
              <div class="col-md-1 col-sm-12">
                <div class="color-gray-6 font-14">Tipo de Licencia:</div>
                <div class="color-black font-16">
                  {{ license.specialtyTypeNameHd ? 'SALUD' : 'MIPE' }}
                </div>
              </div>

              <div class="col-md-2 col-sm-12">
                <div class="color-gray-6 font-14">Estatus:</div>
                <div class="color-black font-16">
                  {{
                    license.specialtyTypeNameHd && !license.isFromHd
                      ? 'Pendiente'
                      : 'Validada'
                  }}
                </div>
              </div>

              <div v-if="license.supervisorFlag" class="col">
                <div class="color-black font-16 mt-2">
                  <check-box
                    class="checkbox"
                    disabled
                    text="Supervisor"
                    :value="license.supervisorFlag"
                  />
                </div>
              </div>
              <div class="col">
                <div class="float-right">
                  <drop-down :items="actionItems(license)" />
                </div>
              </div>
            </div>
          </div>
        </custom-callout>
        <specialist-license-form
          v-if="license.showEditLicense"
          v-model="model"
          :activate-specialist-license-filter-by-health-department="
            activateSpecialistLicenseFilterByHealthDepartment
          "
          :age-ranges="ageRanges"
          class="bg-secondary-3 rounded pb-2"
          :excluded-specialty-types="excludedSpecialtyTypes"
          :specialty-types="specialtyTypes"
          @cancel="onCancel"
          @delete-document="onDeleteDocument"
          @delete-document-by-type="onDeleteDocumentByType"
          @download-document="onDownloadDocument"
          @save="onSave"
        />
      </div>
      <div v-if="!isEditing && !isAdding" class="d-flex flex-row-reverse mt-3">
        <action-button
          i="fas fa-file-medical fa-lg"
          text="Añadir"
          variant="success"
          @click.native.prevent="showForm(null)"
        />
      </div>
    </div>
    <div v-else>
      <div v-if="!isEditing && !isAdding" class="bg-secondary-3 rounded p-5">
        <div class="text-center">
          <action-button
            class="w-50 py-2 btn-editing"
            i="fas fa-file-plus pr-4 text-green"
            text="Presione aquí para añadir Licencia"
            @click.native.prevent="showForm(null)"
          />
        </div>
      </div>
    </div>

    <specialist-license-form
      v-if="isAdding"
      v-model="model"
      :activate-specialist-license-filter-by-health-department="
        activateSpecialistLicenseFilterByHealthDepartment
      "
      :age-ranges="ageRanges"
      class="bg-secondary-3 rounded pb-2"
      :excluded-specialty-types="excludedSpecialtyTypes"
      :specialty-types="specialtyTypes"
      @cancel="onCancel"
      @delete-document="onDeleteDocument"
      @delete-document-by-type="onDeleteDocumentByType"
      @download-document="onDownloadDocument"
      @save="onSave"
    />
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';
import SpecialistLicenseForm from './SpecialistLicense.vue';
import CheckBox from 'CheckBox';
import SectionTitle from 'SectionTitle';
import { getByParameterName } from '@/services/common/sysparameter.api';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

export default {
  name: 'SpecialistLicenses',
  components: {
    ActionButton,
    CustomCallout,
    DropDown,
    CheckBox,
    SpecialistLicenseForm,
    SectionTitle,
  },
  mixins: [alertMixin],
  props: {
    specialistId: {
      type: Number,
      default: () => null,
    },
    specialtyTypes: {
      type: Array,
      default: () => [],
    },
    ageRanges: {
      type: Array,
      default: () => [],
    },
    documentTypes: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    showLicenseForm: {
      type: Boolean,
      default: () => false,
    },
    specialistInfo: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      model: {
        specialistLicenseId: null,
        specialistId: null,
        specialtyTypeId: null,
        specialtyTypeName: null,
        licenseNo: null,
        licenseExpireDate: null,
        backgroundExpireDate: null,
        licenseDocumentExpiryonDate: null,
        ageRangeCd: null,
        ageRangeName: null,
        provisionalFlag: false,
        supervisorFlag: false,
        signLanguageFlag: false,
        englishFlag: false,
        documents: [],
      },
      isAdding: false,
      activateSpecialistLicenseFilterByHealthDepartment: false,
    };
  },
  async created() {
    const { data } = await getByParameterName(
      'ActivateRPSpecialistLicenseFilterByHealthDepartment'
    );
    this.activateSpecialistLicenseFilterByHealthDepartment =
      data.value?.toLowerCase() === 'true';
  },
  computed: {
    licenses() {
      return [...this.value];
    },
    isEditing() {
      return (
        this.licenses &&
        this.licenses.length &&
        this.licenses.filter((x) => x.showEditLicense).length > 0
      );
    },
    excludedSpecialtyTypes() {
      return [...this.licenses.map((x) => x.specialtyTypeId)];
    },
    hasLicenses() {
      return this.licenses.length > 0;
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: 'Descargar Documentos',
            icon: 'fas fa-file-download color-green',
            border: item.specialistLicenseId ? 'bottom' : '',
            visible: Boolean(item.specialistLicenseId),
            click: () => this.$emit('download-documents', item),
          },
          {
            label: 'Editar',
            icon: 'fas fa-edit color-green',
            border:
              this.licenses.length > 1 &&
              this.userHasPermissions('rempro.specialist.deletelicense')
                ? 'bottom'
                : '',
            visible: this.userHasPermissions('rempro.specialist.editlicense'),
            click: () => this.showForm(item),
          },
          {
            label: 'Eliminar',
            icon: 'fas fa-times-circle color-red',
            visible:
              this.licenses.length > 1 &&
              this.userHasPermissions('rempro.specialist.deletelicense'),
            click: () => this.$emit('delete', item),
          },
        ];
      };
    },
    hasDocumentExpired() {
      return (documentExpireDate) => {
        return new Date(documentExpireDate) < new Date();
      };
    },
  },
  methods: {
    async showForm(item) {
      // Validar campos de dirección si es un especialista ya creado
      if (this.specialistId && !this.validateAddressFields()) {
        // Emitir evento para que el padre dispare la validación del formulario
        this.$emit('validate-address-fields');
        this.ShowToast(
          'Alerta',
          'Debe completar los campos requeridos',
          'error'
        );
        return;
      }

      this.cleanModel();
      this.isAdding = item === null;

      const licenseToEdit = this.value.find(
        (x) => x.specialistLicenseId === item?.specialistLicenseId
      );
      if (licenseToEdit) licenseToEdit.showEditLicense = true;

      if (item) {
        this.model = {
          ...item,
          documents: this.documentTypes.map((x) => {
            const document = item.documents.find(
              (y) => y.specialistLicenseDocumentTypeId === x.id
            );
            if (document) return document;
            return {
              specialistLicenseDocumentId: null,
              specialistLicenseId: null,
              specialistLicenseDocumentTypeId: x.id,
              specialistLicenseDocumentTypeName: x.name,
              documentId: null,
              attachment: null,
            };
          }),
        };
      }
    },
    validateAddressFields() {
      if (!this.specialistInfo) return true;

      const {
        postalAddressLine1,
        postalMunicipalityId,
        postalZipCode,
      } = this.specialistInfo;

      return (
        postalAddressLine1 &&
        postalAddressLine1.trim() !== '' &&
        postalMunicipalityId &&
        postalZipCode &&
        postalZipCode.trim() !== ''
      );
    },
    onCancel() {
      this.cleanModel();
    },
    cleanModel() {
      this.isAdding = false;
      this.value.forEach((license) => {
        license.showEditLicense = false;
      });

      this.model = {
        specialistLicenseId: null,
        specialistId: null,
        specialtyTypeId: null,
        specialtyTypeName: null,
        licenseNo: null,
        licenseExpireDate: null,
        ageRangeCd: null,
        ageRangeName: null,
        provisionalFlag: null,
        supervisorFlag: false,
        documents: this.documentTypes.map((x) => {
          return {
            specialistLicenseDocumentId: null,
            specialistLicenseId: null,
            specialistLicenseDocumentTypeId: x.id,
            specialistLicenseDocumentTypeName: x.name,
            documentId: null,
            attachment: null,
          };
        }),
      };
    },
    onSave() {
      let license = { ...this.model };
      if (this.specialistId) {
        license.specialistId = this.specialistId;
        this.$emit('update-specialist-info');
        this.$emit('save', license);
      } else {
        let allLicenses = [...this.licenses];
        if (license.guid) {
          const index = allLicenses.findIndex((x) => x.guid === license.guid);
          allLicenses.splice(index, 1, license);
        } else {
          license.guid = this.builtUniqueId();
          allLicenses.push(license);
        }
        this.$emit('input', allLicenses);
      }
      this.onCancel();
    },
    onDownloadDocument(document) {
      this.$emit('download-document', document);
    },
    onDeleteDocument(document, license) {
      this.$emit('delete-document', document, license);
    },
    onDeleteDocumentByType(document, typeId) {
      this.$emit('delete-document-by-type', document, typeId);
    },
    builtUniqueId() {
      return (
        Date.now().toString(36) +
        Math.random()
          .toString(36)
          .substr(2)
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.btn-editing {
  border: 1px solid #3f5b603d;
}
</style>
